<template>
  <div class="project-tab-content">
    <!-- Project Hero -->
    <div class="project-hero">
      <div class="project-icon-large">
        <i v-if="project.icon" :class="project.icon"></i>
      </div>
      <h2 class="project-title">{{ project.title }}</h2>
      <p class="project-tagline">{{ project.tagline }}</p>
    </div>

    <!-- Project Overview -->
    <div class="project-section">
      <h3 class="section-heading">
        <i class="fas fa-info-circle"></i>
        Project Overview
      </h3>
      <p class="project-description">{{ project.description }}</p>
    </div>

    <!-- What You'll Build -->
    <div class="project-section">
      <h3 class="section-heading">
        <i class="fas fa-check-circle"></i>
        What You'll Build
      </h3>
      <ul class="build-list">
        <li v-for="(item, index) in project.buildItems" :key="index">{{ item }}</li>
      </ul>
    </div>

    <!-- Tech Stack with Auto-Slide -->
    <div class="project-section">
      <h3 class="section-heading">
        <i class="fas fa-code"></i>
        Technologies Used
      </h3>
      <div class="tech-slider-wrapper">
        <div class="tech-slider">
          <div class="tech-track">
            <!-- Original tools -->
            <div v-for="(tech, idx) in project.technologies" :key="`tech-${idx}`" class="tech-item">
              <div class="tech-logo-box">
                <img v-if="tech.logo" :src="tech.logo" :alt="tech.name" class="tech-logo" />
                <i v-else-if="tech.icon" :class="tech.icon" class="tech-icon"></i>
              </div>
              <span class="tech-name">{{ tech.name }}</span>
            </div>
            <!-- Duplicate for seamless loop -->
            <div v-for="(tech, idx) in project.technologies" :key="`tech-dup-${idx}`" class="tech-item">
              <div class="tech-logo-box">
                <img v-if="tech.logo" :src="tech.logo" :alt="tech.name" class="tech-logo" />
                <i v-else-if="tech.icon" :class="tech.icon" class="tech-icon"></i>
              </div>
              <span class="tech-name">{{ tech.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Key Features -->
    <div class="project-section">
      <h3 class="section-heading">
        <i class="fas fa-star"></i>
        Key Features
      </h3>
      <div class="features-grid">
        <div v-for="(feature, index) in project.features" :key="index" class="feature-card">
          <div class="feature-icon">
            <i :class="feature.icon"></i>
          </div>
          <h4 class="feature-title">{{ feature.title }}</h4>
          <p class="feature-description">{{ feature.description }}</p>
        </div>
      </div>
    </div>

    <!-- Project Stats -->
    <div class="project-stats-bar">
      <div class="stat-item">
        <i class="fas fa-clock"></i>
        <span class="stat-label">Duration</span>
        <span class="stat-value">{{ project.duration }}</span>
      </div>
      <div class="stat-item">
        <i class="fas fa-signal"></i>
        <span class="stat-label">Difficulty</span>
        <span class="stat-value">{{ project.difficulty }}</span>
      </div>
      <div class="stat-item">
        <i class="fas fa-layer-group"></i>
        <span class="stat-label">Prerequisites</span>
        <span class="stat-value">{{ project.prerequisites }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Tech {
  logo?: string
  icon?: string
  name: string
}

interface Feature {
  icon: string
  title: string
  description: string
}

interface Project {
  icon: string
  title: string
  tagline: string
  description: string
  buildItems: string[]
  technologies: Tech[]
  features: Feature[]
  duration: string
  difficulty: string
  prerequisites: string
}

defineProps<{
  project: Project
}>()
</script>

<style scoped>
.project-tab-content {
  padding: 0;
}

/* Project Hero */
.project-hero {
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(168, 85, 247, 0.05));
  border-radius: 16px;
  margin-bottom: 32px;
}

.project-icon-large {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  box-shadow: 0 8px 20px rgba(139, 92, 246, 0.3);
}

.project-icon-large i {
  font-size: 40px;
  color: white;
}

.project-title {
  font-size: 32px;
  font-weight: 800;
  color: #1E293B;
  margin: 0 0 12px 0;
  line-height: 1.2;
}

.project-tagline {
  font-size: 18px;
  color: #64748B;
  margin: 0;
  line-height: 1.5;
}

/* Project Sections */
.project-section {
  margin-bottom: 40px;
}

.section-heading {
  font-size: 20px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 2px solid rgba(139, 92, 246, 0.2);
}

.section-heading i {
  color: #3b82f6;
  font-size: 22px;
}

.project-description {
  font-size: 16px;
  color: #475569;
  line-height: 1.8;
  margin: 0;
}

/* Build List */
.build-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 12px;
}

.build-list li {
  font-size: 15px;
  color: #475569;
  padding: 12px 16px 12px 40px;
  background: white;
  border-radius: 8px;
  border-left: 3px solid #3b82f6;
  position: relative;
  line-height: 1.6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.build-list li::before {
  content: '✓';
  position: absolute;
  left: 14px;
  color: #3b82f6;
  font-weight: bold;
  font-size: 18px;
}

/* Tech Stack Slider */
.tech-slider-wrapper {
  overflow: hidden;
  padding: 20px 0;
  background: linear-gradient(90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.5) 5%,
    rgba(255,255,255,0.5) 95%,
    rgba(255,255,255,0) 100%);
}

.tech-slider {
  overflow: hidden;
}

.tech-track {
  display: flex;
  gap: 24px;
  animation: slideLeft 35s linear infinite;
  width: fit-content;
}

.tech-track:hover {
  animation-play-state: paused;
}

@keyframes slideLeft {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.tech-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: white;
  border-radius: 10px;
  border: 1px solid rgba(139, 92, 246, 0.15);
  min-width: 100px;
  transition: all 0.3s ease;
}

.tech-item:hover {
  transform: translateY(-4px);
  border-color: rgba(139, 92, 246, 0.4);
  box-shadow: 0 6px 12px rgba(139, 92, 246, 0.15);
}

.tech-logo-box {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(139, 92, 246, 0.05);
  border-radius: 8px;
  padding: 6px;
}

.tech-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.tech-icon {
  font-size: 28px;
  color: #3b82f6;
}

.tech-name {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-align: center;
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.feature-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid rgba(139, 92, 246, 0.15);
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  border-color: rgba(139, 92, 246, 0.3);
  box-shadow: 0 8px 16px rgba(139, 92, 246, 0.15);
}

.feature-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.feature-icon i {
  font-size: 20px;
  color: white;
}

.feature-title {
  font-size: 16px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 8px 0;
}

.feature-description {
  font-size: 14px;
  color: #64748B;
  margin: 0;
  line-height: 1.6;
}

/* Project Stats Bar */
.project-stats-bar {
  display: flex;
  justify-content: space-around;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(168, 85, 247, 0.08));
  border-radius: 12px;
  margin-top: 32px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  flex: 1;
}

.stat-item i {
  font-size: 28px;
  color: #3b82f6;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 16px;
  font-weight: 700;
  color: #1E293B;
}

/* Responsive */
@media (max-width: 768px) {
  .features-grid {
    grid-template-columns: 1fr;
  }

  .project-stats-bar {
    flex-direction: column;
  }

  .project-title {
    font-size: 24px;
  }

  .project-tagline {
    font-size: 16px;
  }
}
</style>
